import React, { useMemo, useState } from "react"
import "./SlotForm.scss"
import { SlotResponse, SlotDto, SlotValues } from "../../../models/slot"
import { Formik } from "formik"
import { useTranslation } from "react-i18next"
import ValidatableFormTabs, { ValidateTabRef } from "../../ValidatableFormTabs/ValidatableFormTabs"
import { FormikProps } from "formik/dist/types"
import { WithT } from "i18next"
import FormikSlotForm from "./FormikSlotForm"
import {
    buildCreateSlotRequest,
    buildUpdateSlotRequest,
    getInitialValues
} from "../../../utility/knowledgeBase/slotForm"
import { useAppSelector } from "../../../store/hooks"
import { selectCurrentProjectId } from "../../../store/projects/selectors"
import * as Yup from "yup"
import { projectIdRegex } from "../../../utility/common/projectIdRegex"
import { formTranslation } from "../../../locales/form"
import { useCreateSlotMutation, useUpdateSlotMutation } from "../../../api/controllers/slot"
import { EntitySlotType } from "../../../models/workshop/form"

const tNamespace = "slot:"
const tErrorNamespace = "slot:error."
export const tSlotFormNamespace = "slot:form."

export interface SlotFormProps {
    slot?: SlotDto
    onAddSlot?: (slotId: string, slotTitle: string) => void
    onUpdateSlot?: (slot: SlotDto) => void
    addSlotToApp?: (slot: EntitySlotType) => void
    slotIds?: string[]
    isEditMode?: boolean
}

export interface FormikSlotFormProps extends FormikProps<SlotValues>, WithT {
    slot?: SlotDto
    isLoading: boolean
    disabled?: boolean
}

const SlotForm: React.FC<SlotFormProps> = props => {
    const { t } = useTranslation()
    const { slot, onAddSlot, onUpdateSlot, addSlotToApp, slotIds = [], isEditMode } = props
    const projectId = useAppSelector(selectCurrentProjectId)
    const [createSlot, { isLoading: isCreating }] = useCreateSlotMutation()
    const [updateSlot, { isLoading: isUpdating }] = useUpdateSlotMutation()
    const isLoading = slot ? isUpdating : isCreating

    const [isDictionary, setIsDictionary] = useState(false)

    const tabEntries = useMemo(() => {
        const tabs = [
            { key: "general", value: t(`${tNamespace}general`) },
            { key: "advanced", value: t(`${tNamespace}advanced`) }
        ]
        isDictionary && tabs.push({ key: "dictionary", value: t(`${tNamespace}dictionary-values`) })
        return tabs
    }, [isDictionary, t])

    const handleSubmit = (values: SlotValues) => {
        if (!projectId) return

        if (addSlotToApp) {
            const applicationSlot = {
                ...buildCreateSlotRequest(values),
                Id: values.ExternalId
            }
            addSlotToApp(applicationSlot)
            return
        }

        if (slot) {
            updateSlot({
                projectId,
                slotId: slot.Id,
                body: buildUpdateSlotRequest(values),
                callback: ({ Slot }: SlotResponse) => {
                    onUpdateSlot && onUpdateSlot(Slot)
                }
            })
        } else {
            createSlot({
                projectId,
                body: buildCreateSlotRequest(values),
                callback: ({ Slot }: SlotResponse) => {
                    onAddSlot && onAddSlot(Slot.ExternalId, Slot.Title)
                }
            })
        }
    }

    const initialValues = useMemo(() => getInitialValues(slot), [slot])

    if (!projectId || !initialValues) return null

    return (
        <ValidatableFormTabs id="slot-settings-tabs" alwaysReload={false} entries={tabEntries}>
            {(validateTabs: (tabs: ValidateTabRef[]) => void) => (
                <Formik
                    validationSchema={Yup.object().shape({
                        Title: Yup.string().requiredExcludeEmpty(formTranslation.titleRequired),
                        ...(!slot?.ExternalId && {
                            ExternalId: Yup.string()
                                .requiredExcludeEmpty(formTranslation.idRequired)
                                .matches(projectIdRegex, {
                                    message: `${tErrorNamespace}invalid-id`,
                                    excludeEmptyString: true
                                })
                                .notOneOf(slotIds, `${tErrorNamespace}unique-id`)
                        }),
                        Domain: Yup.array().of(
                            Yup.object().shape({
                                Id: Yup.string().when("IsEmpty", {
                                    is: value => !value,
                                    then: Yup.string()
                                        .requiredExcludeEmpty(formTranslation.idRequired)
                                        .matches(projectIdRegex, {
                                            message: `${tErrorNamespace}invalid-id`,
                                            excludeEmptyString: true
                                        })
                                }),
                                Title: Yup.string().when("IsEmpty", {
                                    is: value => !value,
                                    then: Yup.string().requiredExcludeEmpty(formTranslation.titleRequired)
                                })
                            })
                        )
                    })}
                    enableReinitialize={true}
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                >
                    {formikProps => {
                        const validate = () => {
                            return validateTabs
                        }
                        return (
                            <FormikSlotForm
                                formikProps={formikProps}
                                isDictionary={isDictionary}
                                setIsDictionary={setIsDictionary}
                                tabEntries={tabEntries}
                                slot={slot}
                                onValidateTabs={validate}
                                isLoading={isLoading}
                                disabled={isEditMode === false}
                            />
                        )
                    }}
                </Formik>
            )}
        </ValidatableFormTabs>
    )
}

export default SlotForm
